import React, { memo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import OverlapIcon from './OverlapIcon';

const BlockWrapper = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  cursor: ${(props) => (props.isEditing ? 'text' : 'grab')};
  font-family: 'Nunito Sans', sans-serif;
  font-weight: 700;
  letter-spacing: 0;
  color: ${(props) => props.palette.text};
  overflow: hidden;
  font-size: 16px;
  line-height: 1.5;
  background: ${(props) => props.palette.background};
  box-sizing: border-box;
  border: ${(props) => {
    if (props.isEditing) {
      return `2px solid ${props.palette.accent}`;
    }
    if (props.isSelected) {
      return `2px solid ${props.palette.accent}`;
    }
    return `1px solid ${props.palette.border}`;
  }};
  box-shadow: ${(props) => {
    if (props.isDragging) {
      return '0 4px 12px rgba(0, 0, 0, 0.12)';
    }
    if (props.isEditing) {
      return `0 0 0 2px ${props.palette.accent}33`;
    }
    if (props.isSelected && !props.isEditing) {
      return `0 0 0 1px ${props.palette.accent}33`;
    }
    return 'none';
  }};
  transition: ${(props) => (props.isDragging
    ? 'none' /* Instant during drag for performance */
    : 'border 0.1s ease, box-shadow 0.1s ease, opacity 0.1s ease')};

  /* Performance optimizations for smooth dragging */
  will-change: ${(props) => (props.isDragging ? 'transform' : 'auto')};
  transform: translateZ(0); /* Force GPU acceleration */
  backface-visibility: hidden; /* Prevent flickering */
  opacity: ${(props) => (props.isDragging ? '0.75' : '1')};

  &:hover {
    opacity: ${(props) => (props.isDragging ? '0.75' : '1')};
  }
`;

const BlockText = styled.div`
  flex: 1;
  text-align: center;
  font-weight: 700;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit;
  position: relative;
  z-index: 2;
  cursor: inherit;
`;

const BlockInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: inherit;
  text-align: center;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.5;
  font-family: 'Nunito Sans', sans-serif;
  outline: none;
  cursor: text;
  position: relative;
  z-index: 2;
  caret-color: #444444;
  pointer-events: auto;
  min-width: 0;
  max-width: 100%;

  &::placeholder {
    color: #bdbdbd;
  }
`;

const OverlapBadge = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  pointer-events: none;
  z-index: 3;
`;

/**
 * BlockContent - Presentational component for the visual content of a plan block.
 * Handles the display of block name (text or input), overlap indicators, and visual states.
 *
 * This component is purely presentational and receives all data via props.
 * State management and event handling are managed by the parent PlanBlock component.
 */
const BlockContent = memo(forwardRef(({
  // Visual state
  palette,
  isEditing,
  isSelected,
  isDragging,

  // Block data
  name,
  tempName,
  isTextTruncated,

  // Overlap state
  hasOverlap,
  showOverlapIndicator,
  overlappingCount,

  // Refs
  textRef,
  inputRef,

  // Event handlers
  onClick,
  onMouseDownCapture,
  onDoubleClick,
  onTouchEnd,
  onContextMenu,
  onNameChange,
  onInputBlur,
  onInputKeyDown,
  onInputClick,
  onInputMouseDown,
}, ref) => (
  <BlockWrapper
    ref={ref}
    palette={palette}
    onClick={onClick}
    onMouseDownCapture={onMouseDownCapture}
    onDoubleClick={onDoubleClick}
    onTouchEnd={onTouchEnd}
    onContextMenu={onContextMenu}
    isEditing={isEditing}
    isSelected={isSelected}
    hasOverlap={hasOverlap}
    isDragging={isDragging}
  >
    {showOverlapIndicator && !isEditing && (
      <OverlapBadge
        title={`Overlaps with ${overlappingCount} block${
          overlappingCount > 1 ? 's' : ''
        }`}
      >
        <OverlapIcon
          count={overlappingCount + 1}
          backgroundColor={palette.background}
        />
      </OverlapBadge>
    )}

    {isEditing ? (
      <BlockInput
        ref={inputRef}
        type='text'
        value={tempName}
        onChange={onNameChange}
        onBlur={onInputBlur}
        onClick={onInputClick}
        onMouseDown={onInputMouseDown}
        onKeyDown={onInputKeyDown}
        placeholder='Block name'
      />
    ) : (
      <BlockText
        ref={textRef}
        title={isTextTruncated ? name : undefined}
      >
        {name}
      </BlockText>
    )}
  </BlockWrapper>
)));

BlockContent.displayName = 'BlockContent';

BlockContent.propTypes = {
  // Visual state
  palette: PropTypes.shape({
    background: PropTypes.string.isRequired,
    border: PropTypes.string.isRequired,
    accent: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  isEditing: PropTypes.bool,
  isSelected: PropTypes.bool,
  isDragging: PropTypes.bool,

  // Block data
  name: PropTypes.string,
  tempName: PropTypes.string,
  isTextTruncated: PropTypes.bool,

  // Overlap state
  hasOverlap: PropTypes.bool,
  showOverlapIndicator: PropTypes.bool,
  overlappingCount: PropTypes.number,

  // Refs (passed as props for use with forwardRef)
  textRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),

  // Event handlers
  onClick: PropTypes.func,
  onMouseDownCapture: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onTouchEnd: PropTypes.func,
  onContextMenu: PropTypes.func,
  onNameChange: PropTypes.func,
  onInputBlur: PropTypes.func,
  onInputKeyDown: PropTypes.func,
  onInputClick: PropTypes.func,
  onInputMouseDown: PropTypes.func,
};

BlockContent.defaultProps = {
  isEditing: false,
  isSelected: false,
  isDragging: false,
  name: '',
  tempName: '',
  isTextTruncated: false,
  hasOverlap: false,
  showOverlapIndicator: false,
  overlappingCount: 0,
  textRef: null,
  inputRef: null,
  onClick: undefined,
  onMouseDownCapture: undefined,
  onDoubleClick: undefined,
  onTouchEnd: undefined,
  onContextMenu: undefined,
  onNameChange: undefined,
  onInputBlur: undefined,
  onInputKeyDown: undefined,
  onInputClick: undefined,
  onInputMouseDown: undefined,
};

export default BlockContent;

export {
  BlockWrapper, BlockText, BlockInput, OverlapBadge,
};
